import { showAlertCallout } from "@/shared/alerts";
import {
  getGetUserListPaginatedQueryKey,
  useInviteUser,
} from "@/spz-api/user/user";
import { UserAccountType } from "@spz/shared-domain";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { INVITE_USER_FORM_DEFAULT_VALUES } from "../forms/constants/invite-user-form.constants";
import {
  type InviteUserFormValues,
  inviteUserResolver,
} from "../forms/schemas/invite-user-form.schema";

export const useInviteUserFormViewModel = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const resolver = inviteUserResolver(t);
  const form = useForm<InviteUserFormValues>({
    resolver,
    defaultValues: INVITE_USER_FORM_DEFAULT_VALUES,
    mode: "onBlur",
  });

  const inviteUserMutation = useInviteUser({
    mutation: {
      onSuccess: () => {
        showAlertCallout({
          type: "success",
          title: t("user_invited_successfully"),
        });

        queryClient.invalidateQueries({
          queryKey: getGetUserListPaginatedQueryKey({}),
        });
        form.reset();
      },
      onError: (error: AxiosError) => {
        const isConflictError = error.response?.status === 409;
        const title = isConflictError
          ? t("user_already_exists")
          : t("error_inviting_user");

        showAlertCallout({
          type: "critical",
          title,
        });
      },
    },
  });

  const onSubmit: SubmitHandler<InviteUserFormValues> = async (
    data: InviteUserFormValues,
  ) => {
    try {
      await inviteUserMutation.mutate({ data });
    } catch (error) {
      showAlertCallout({
        type: "critical",
        title: t("error_inviting_user"),
      });
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting: inviteUserMutation.isPending,
    accountTypeOptions: Object.values(UserAccountType).map((type) => {
      return {
        id: type,
        value: type,
        label: t(`user_account_type_${type}`),
      };
    }),
  };
};
